/**
 * Halloween painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenPaintingIdeasPack: ToilePack = {
    slug: 'halloween-painting-ideas',
    category: 'craft',
    name: 'Halloween painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'house', label: 'Haunted house', subject: 'a canvas of a haunted house on a hill under a huge orange moon with bats'},
        {key: 'cat', label: 'Moon cat', subject: 'a canvas of a black cat sitting on a crescent moon against a purple night'},
        {key: 'ghosts', label: 'Little ghosts', subject: 'a canvas of little white ghosts floating over a pumpkin patch at dusk'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'painted in easy cartoon-style acrylic with flat colour and bold black outlines', palette: 'Bright orange, purple and lime against deep night blue.', framing: 'The canvas on a wooden table easel, shot straight on at eye level and centred.'},
        {key: 'blackcanvas', label: 'Black canvas', style: 'painted on a black canvas so the bright colours glow against the dark ground', palette: 'Black ground with glowing orange, white and violet highlights.', framing: 'Propped on a mantel among small pumpkins and dried autumn leaves, shot at eye level.'},
        {key: 'storybook', label: 'Storybook', style: 'painted in a soft storybook style with dry-brush texture and gentle blended skies', palette: 'Dusky plum, teal and pumpkin orange, gently muted.', framing: 'Hung on a front door under a twig wreath, shot straight on at eye level.'},
    ],
    directions: ['witch hat', 'candy corn', 'skeleton'],
    directionHint: 'try: witch hat',
    seo: {
        title: 'Halloween Painting Ideas: Haunted House, Moon Cat, Ghosts',
        metaDescription: 'Nine Halloween painting ideas, spooky but friendly: a haunted house, a black cat on a crescent moon and little ghosts, in three styles.',
        h1: 'Halloween painting ideas: haunted house, black cat on the moon, little ghosts',
        tagline: 'Nine Halloween canvases, three friendly spooky subjects, each in easy acrylic, on black canvas or in storybook style.',
        intro: 'Halloween canvases are painted for the mantel and the front door, spooky but friendly. These nine show three subjects: a haunted house on a hill under a huge orange moon with bats, a black cat sitting on a crescent moon against a purple night, and little white ghosts floating over a pumpkin patch at dusk. Type another subject, such as a witch hat or a skeleton, and the same friendly look carries it.',
        useCases: [
            'a mantel display',
            'a front-door canvas',
            'a classroom Halloween wall',
            'a party decoration',
        ],
        faq: [
            {q: 'Are these scary?', a: 'No. The look is spooky but friendly, with bright orange, lime and purple and cartoon outlines.'},
            {q: 'Which style uses black?', a: 'The black canvas style keeps the ground black, so the bright colors glow against it.'},
            {q: 'What is the storybook style?', a: 'Soft dry-brush texture and gentle blended skies in dusky plum, teal and pumpkin orange.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the friendly Halloween look carries it.'},
        ],
    },
}
